import { AdvancedSelect } from '../../../../widgets/select';
import { SearchInput } from '../../../../widgets/input';
import { TableContentLoader } from '../../../../widgets/table';
import { useInfiniteScroll } from '../../../../widgets/hooks/useInfiniteScroll';
import { debounce } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';
import { ContainerCombo, SearchContainer } from 'src/components/pages/productConfiguration/steps/6-done/connectModelDialog';
import Loader from 'src/components/widgets/loader';
import { Center } from 'src/shared/globals';
import { createFakePalette, T } from 'src/shared/helpers';
import styled from 'styled-components';
import { PaletteInput } from '../__generated__/paletteColorsPageSaveMutation.graphql';
import { pantoneLibraryColorsContainer_colors$key } from './__generated__/pantoneLibraryColorsContainer_colors.graphql';
import { pantoneLibraryColorsContainer_colorsQuery } from './__generated__/pantoneLibraryColorsContainer_colorsQuery.graphql';
import { PantoneLibraryContent } from './pantoneLibraryContent';

const StickyContainerCombo = styled(ContainerCombo)`
    position: sticky;
    top: 0px;
    z-index: 10;
    background-color: white;
    padding-bottom: 16px;
    margin-bottom: 0;
`;
interface Props {
    onSelectedColorsChange: (colors: any[]) => void;
    singleSelection?: boolean;
    scrollContainer?: React.MutableRefObject<HTMLDivElement | null>;
    onSelectedPaletteChange?: (palette: PaletteInput | null) => void;
}
const pantoneLibraryColorsContainerQuery = graphql`
    fragment pantoneLibraryColorsContainer_colors on Query
    @refetchable(queryName: "pantoneLibraryColorsContainerQuery")
    @argumentDefinitions(
        first: { type: "Int", defaultValue: 70 }
        after: { type: "String", defaultValue: "1" }
        search: { type: "String", defaultValue: null }
        id: { type: "String", defaultValue: null }
        paletteTypeID: { type: "Int", defaultValue: 1 }
        onlyDefaultColors: { type: "Boolean", defaultValue: null }
        orderBy: { type: "String", defaultValue: "DESC" }
        useConversionTable: { type: "Boolean", defaultValue: null }
    ) {
        paletteColors(
            first: $first
            after: $after
            search: $search
            id: $id
            paletteTypeID: $paletteTypeID
            onlyDefaultColors: $onlyDefaultColors
            orderBy: $orderBy
            useConversionTable: $useConversionTable
        ) @connection(key: "pantoneLibraryColorsContainerColorsQuery_paletteColors") {
            pageInfo {
                hasNextPage
                endCursor
            }
            edges {
                cursor
                node {
                    id
                    colorID
                    name
                    rgbHexCode
                    cmykHexCode
                    spotColorName
                }
            }
        }
        palettesWithColors {
            edges {
                node {
                    id
                    name
                    useConversionTable
                    paletteTypeID
                    isZakekeDefault
                }
            }
        }
    }
`;

const PantoneLibraryColorsContainer: FC<Props> = ({ onSelectedColorsChange, singleSelection, scrollContainer, onSelectedPaletteChange }) => {
    const [searchFilter, setSearchFilter] = useState('');
    const [search, setSearch] = useState('');
    const previousColorsCountRef = useRef(0);
    const isLoadingMoreRef = useRef(false);
    const [selectedColors, setSelectedColors] = useState<any[]>([]);

    const query = useLazyLoadQuery<pantoneLibraryColorsContainer_colorsQuery>(
        graphql`
            query pantoneLibraryColorsContainer_colorsQuery(
                $id: String
                $search: String
                $onlyDefaultColors: Boolean
                $orderBy: String
                $useConversionTable: Boolean
            ) {
                ...pantoneLibraryColorsContainer_colors
                    @arguments(id: $id, search: $search, onlyDefaultColors: $onlyDefaultColors, orderBy: $orderBy, useConversionTable: $useConversionTable)
            }
        `,
        {
            id: null,
            search: null,
            onlyDefaultColors: null,
            orderBy: 'DESC',
            useConversionTable: true
        },
        { fetchPolicy: 'store-or-network' }
    );

    const { data, loadNext, refetch, hasNext, isLoadingNext } = usePaginationFragment<
        pantoneLibraryColorsContainer_colorsQuery,
        pantoneLibraryColorsContainer_colors$key
    >(pantoneLibraryColorsContainerQuery, query);

    const currentColors = useMemo(() => data?.paletteColors?.edges?.map((e) => e.node) ?? [], [data?.paletteColors]);
    const currentPalettes = useMemo(() => data?.palettesWithColors, [data?.palettesWithColors]);

    const allPalettes = useMemo(() => createFakePalette('guid://Zakeke/Palette/FullLibrary', 'Full Library'), [currentPalettes]);

    const defaultPalettes = useMemo(
        () => createFakePalette('guid://Zakeke/Palette/DefaultLibrary', 'Full palette (include all the above - 20091 colors)'),
        [currentPalettes]
    );

    const options = useMemo(() => {
        const palettes =
            currentPalettes?.edges
                ?.filter((edge) => edge.node.paletteTypeID === 1)
                .map((edge) => ({
                    value: edge.node.id,
                    label: edge.node.name
                })) ?? [];

        palettes.push({
            value: defaultPalettes.id!,
            label: defaultPalettes.name
        });

        palettes.unshift({
            value: allPalettes.id!,
            label: allPalettes.name
        });

        return palettes;
    }, [currentPalettes]);

    const palettes = useMemo(() => {
        const palettes: PaletteInput[] =
            currentPalettes?.edges?.map((edge) => ({
                id: edge.node.id,
                name: edge.node.name,
                useConversionTable: edge.node.useConversionTable,
                paletteTypeID: edge.node.paletteTypeID,
                isZakekeDefault: edge.node.isZakekeDefault,
                colors: []
            })) ?? [];

        palettes.push(defaultPalettes);
        palettes.unshift(allPalettes);

        return palettes;
    }, [currentPalettes, allPalettes]);

    const [selectedPalette, setSelectedPalette] = useState<PaletteInput>(allPalettes);
    const [isPending, startTransition] = useTransition();

    const setSearchDebounced = useCallback(
        debounce((filter: string) => {
            setSearchFilter(filter);
        }, 300),
        []
    );

    const handleSearchColor = (text: string) => {
        setSearchDebounced(text);
        setSearch(text);
    };

    const handleSelectedColorsChange = useCallback(
        (colors: any[]) => {
            setSelectedColors(colors);
            onSelectedColorsChange(colors);
        },
        [onSelectedColorsChange]
    );

    // Refetch with new palette parameters to reset the connection
    useEffect(() => {
        startTransition(() => {
            refetch(
                {
                    id:
                        selectedPalette?.id === 'guid://Zakeke/Palette/FullLibrary' || selectedPalette?.id === 'guid://Zakeke/Palette/DefaultLibrary'
                            ? null
                            : (selectedPalette?.id ?? null),
                    search: searchFilter || null,
                    onlyDefaultColors: selectedPalette?.id === 'guid://Zakeke/Palette/DefaultLibrary' ? true : null,
                    orderBy: 'DESC',
                    useConversionTable: true
                },
                { fetchPolicy: 'store-or-network' }
            );
        });
    }, [selectedPalette?.id, searchFilter]);

    // Reset scroll position when palette changes
    useEffect(() => {
        if (scrollContainer?.current) {
            scrollContainer.current.scrollTop = 0;
        }
    }, [selectedPalette?.id]);

    const handlePaletteChange = useCallback(
        (item: { value: string | null | undefined; label: string } | null) => {
            const selectedPaletteId = item?.value || null;
            const foundPalette = palettes.find((p) => p.id === selectedPaletteId) || allPalettes;
            setSelectedPalette(foundPalette);
            onSelectedPaletteChange?.(foundPalette);
            // Reset the ref when palette changes
            previousColorsCountRef.current = 0;
        },
        [palettes, allPalettes, defaultPalettes, onSelectedPaletteChange]
    );

    // Track when we're loading more and preserve scroll position
    useEffect(() => {
        if (isLoadingNext && !isLoadingMoreRef.current) {
            isLoadingMoreRef.current = true;
            previousColorsCountRef.current = currentColors.length;
        } else if (!isLoadingNext && isLoadingMoreRef.current) {
            isLoadingMoreRef.current = false;
        }
    }, [isLoadingNext, currentColors.length]);

    useInfiniteScroll(
        () => {
            loadNext(70);
        },
        hasNext,
        scrollContainer?.current,
        isLoadingNext
    );

    return (
        <>
            <StickyContainerCombo>
                <SearchContainer gridTemplateColumns='1fr 448px'>
                    <AdvancedSelect
                        options={options}
                        className='comboControl'
                        value={options.find((s) => s.value === selectedPalette.id)}
                        placeholder={T._('Full library', 'Admin')}
                        onChange={handlePaletteChange}
                    />
                    <SearchInput fullWidth placeholder={T._('Search...', 'Admin')} value={search} onChange={(e) => handleSearchColor(e.target.value)} />
                </SearchContainer>
            </StickyContainerCombo>

            {isPending ? (
                <TableContentLoader />
            ) : currentColors.length === 0 ? (
                <Center style={{ marginTop: '15px' }}>{T._('No colors found. Please try a different search.', 'Admin')}</Center>
            ) : (
                <>
                    <PantoneLibraryContent
                        onSelectedColorsChange={handleSelectedColorsChange}
                        currentColors={currentColors}
                        singleSelection={singleSelection}
                        selectedColors={selectedColors}
                    />

                    {isPending ||
                        (hasNext && isLoadingNext && (
                            <Center style={{ marginTop: '15px' }}>
                                <Loader />
                            </Center>
                        ))}
                </>
            )}
        </>
    );
};
export default PantoneLibraryColorsContainer;
